import { TpSlValueState } from '@ui/features/Trade/types';
import { Nullable, PossibleBigNumber } from '@ui/types/utility';
import { getEstPnl } from '@ui/utils/trade/getEstPnl';
import { getTakeProfitStopLossPercentage } from '@ui/utils/trade/getTakeProfitStopLossPercentage';
import { debounce, isNil } from 'lodash-es';
import { useCallback, useMemo } from 'react';

import { defaultTpSlValue } from '../store/defaultTpSlValue';

export const useDebounceTpSlInput = ({
  value,
  setValue,
  entryPrice,
  leverage,
  collateralUsd,
  isLong,
  spreadPercentage,
}: {
  value: TpSlValueState;
  setValue: (value: React.SetStateAction<TpSlValueState>) => void;
  entryPrice: Nullable<number>;
  leverage: number;
  collateralUsd: PossibleBigNumber;
  isLong: boolean;
  spreadPercentage: Nullable<number>;
}) => {
  const debouncedCalc = useMemo(
    () =>
      debounce((nextValue: string) => {
        if (isNil(entryPrice) || isNil(spreadPercentage)) return;

        if (nextValue === '') {
          setValue(defaultTpSlValue);
          return;
        }

        const numberInput = Number(nextValue);

        if (!Number.isFinite(numberInput) || numberInput <= 0) {
          setValue((prev) => ({
            ...prev,
            deltaPnl: undefined,
            pnlPercentage: undefined,
          }));
          return;
        }

        const targetPercentage = getTakeProfitStopLossPercentage({
          targetPrice: numberInput,
          entryPrice: entryPrice,
          leverage,
          isLong,
          spreadPercentage,
        });

        setValue((prev) => ({
          ...prev,
          deltaPnl: getEstPnl({
            collateralUsd: collateralUsd,
            targetPercentage,
            isLong,
            spreadPercentage,
          }),
          pnlPercentage: targetPercentage,
        }));
      }, 100),
    [entryPrice, spreadPercentage, leverage, isLong, collateralUsd, setValue],
  );

  return useCallback(
    (nextValue: string) => {
      setValue((prev) => ({
        ...prev,
        priceValue: nextValue,
        isPnlPercentage: false,
      }));
      debouncedCalc(nextValue);
    },
    [setValue, debouncedCalc],
  );
};
